import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import { Box } from '@chakra-ui/react'

import { contentEditableToText, focusAndMoveCaretToEnd } from './utils'

interface TextboxProps {
  isEditing: boolean
  text?: string
  focusOnEdit?: boolean
}

export interface TextboxRef {
  /**
   * Gets the text content from the editable element.
   * @param raw - If true, returns the raw HTML innerHTML. If false (default), returns processed text with <br> tags converted to newlines and HTML tags stripped.
   * @returns The text content as a string
   */
  getText: (raw?: boolean) => string
}

const Textbox = forwardRef<TextboxRef, TextboxProps>(({ isEditing, text, focusOnEdit }, ref) => {
  // Refs
  const editableTextRef = useRef<HTMLDivElement>(null)

  /**
   * When entering editing mode, focus the top text input and move caret to the end.
   *
   * Need to use `useLayoutEffect` to ensure the DOM is updated before focusing.
   */
  useLayoutEffect(() => {
    if (!isEditing) return

    if (editableTextRef.current) {
      editableTextRef.current.innerHTML = text || ''

      if (focusOnEdit) focusAndMoveCaretToEnd(editableTextRef.current)
    }
  }, [isEditing, text, focusOnEdit, ref])

  useImperativeHandle(
    ref,
    () => ({
      getText: (raw = false) => {
        const innerHTML = editableTextRef.current?.innerHTML || ''

        if (raw) {
          return innerHTML
        }

        return contentEditableToText(innerHTML)
      },
    }),
    [],
  )

  return (
    <Box className={['ta-tb-container', isEditing ? 'is-editing' : ''].join(' ')}>
      <Box
        ref={editableTextRef}
        className="ta-tb-editbox"
        contentEditable
        onInput={(e) => {
          if (e.target instanceof HTMLDivElement) {
            e.target.classList.toggle('is-empty', e.target.textContent?.trim() === '')
          }
        }}
      />
      <Box className="ta-tb-text">{text}</Box>
    </Box>
  )
})

export default Textbox
